import type {
  SigningAvailability,
  SigningFailureKind,
} from "../entities/document-workflow";

export interface FailureMessage {
  readonly title: string;
  readonly text: string;
}

/**
 * Что человеку сказать о каждой причине неудачи.
 *
 * Причины не сливаются: плагин, сертификат, устаревший запрос, неверная
 * подпись и недоступная серверная проверка лечатся по-разному.
 */
export const failureMessages: Record<SigningFailureKind, FailureMessage> = {
  capability_disabled: {
    title: "Электронная подпись отключена на сервере",
    text: "Администратор не включил подпись документов. Обратитесь к нему, если подпись нужна.",
  },
  permission_denied: {
    title: "Недостаточно прав для подписи",
    text: "Сервер отказал в операции: у вас нет права подписывать или проверять подписи этого документа.",
  },
  plugin_unavailable: {
    title: "Плагин КриптоПро недоступен в браузере",
    text: "Не найден браузерный плагин КриптоПро (CryptoPro Browser plug-in) либо он не отвечает. Установите плагин и расширение для браузера, включите их для этого сайта и нажмите «Проверить снова». Сервер и проверка подписи здесь ни при чём. Если плагин уже установлен, а сообщение остаётся, обратитесь к администратору: скрипт плагина может быть не подключён к приложению.",
  },
  no_certificates: {
    title: "Нет подходящего сертификата",
    text: "В личном хранилище нет действующего сертификата с закрытым ключом. Подключите носитель с ключом или установите сертификат и проверьте снова. Подписание не начато.",
  },
  certificate_not_found: {
    title: "Сертификат не найден",
    text: "Выбранный сертификат пропал из хранилища — например, извлечён носитель. Проверьте снова и выберите сертификат ещё раз.",
  },
  profile_unsupported: {
    title: "Профиль подписи не поддерживается",
    text: "Этот браузер не умеет создавать профиль подписи, который разрешил сервер.",
  },
  user_cancelled: {
    title: "Подписание отменено",
    text: "Операция отменена в окне КриптоПро. Подпись не создана, версия осталась без подписи.",
  },
  request_expired: {
    title: "Запрос подписи устарел",
    text: "Срок действия одноразового запроса истёк, подпись не сохранена. Начните подписание заново: будет создан новый запрос, старый повторно не используется.",
  },
  signature_invalid: {
    title: "Подпись не принята сервером",
    text: "Сервер признал подпись неверной либо запрос уже был использован. Версия осталась без подписи. Создайте подпись заново.",
  },
  verifier_unavailable: {
    title: "Серверная проверка недоступна",
    text: "Сервер сейчас не может проверить подпись (HTTP 503) и не сохраняет непроверенные подписи. Это не ошибка подписи и не сбой плагина: повторите позже.",
  },
  content_unavailable: {
    title: "Не удалось получить содержимое версии",
    text: "Хранилище не отдало файл для подписи. Подпись не создана, запрос будет выдан заново при следующей попытке.",
  },
  hash_mismatch: {
    title: "Содержимое не совпало с SHA-256 версии",
    text: "Полученный файл отличается от неизменяемой версии, поэтому подписывать его нельзя. Подпись не создана.",
  },
  failed: {
    title: "Не удалось подписать версию",
    text: "Операция не завершилась. Версия осталась без подписи. Повторите попытку.",
  },
};

/** Почему действие подписи недоступно. */
export const unavailableReasons: Record<
  Extract<SigningAvailability, { status: "unavailable" }>["reason"],
  string
> = {
  capabilities_failed:
    "Не удалось получить настройки электронной подписи с сервера, поэтому подписание пока недоступно.",
  capability_disabled:
    "Электронная подпись отключена на сервере. Обратитесь к администратору.",
  permission_denied: "У вас нет права подписывать документы.",
  version_not_ready:
    "Подписать можно только проверенную версию: сейчас проверка не завершена, файл в карантине либо у версии нет SHA-256.",
  no_profile: "Сервер не указал ни одного допустимого профиля подписи.",
  profile_unsupported:
    "Профили подписи, разрешённые сервером, этот браузер создать не может.",
};

export const workingSteps = {
  plugin: "Проверяем плагин КриптоПро…",
  certificates: "Читаем сертификаты…",
  request: "Создаём запрос подписи…",
  content: "Получаем содержимое версии и сверяем SHA-256…",
  sign: "Подписываем в КриптоПро…",
  send: "Отправляем подпись на сервер и ждём проверки…",
} as const;
